<template>
  <!--
    Door-aware plan cards. Presentational: the parent loads the catalogue
    (the same GET /api/cluster/plans Pricing.vue uses, via plans-source.js)
    and passes it in; this component shows only the door's emphasised plans
    and points at /pricing for the rest. A door with no plans (self-managed)
    renders the licence block instead of cards.
  -->
  <div class="dp" :class="{ 'dp--center': align === 'center' }">
    <div v-if="loading" class="dp-grid" aria-busy="true">
      <div v-for="n in skeletonCount" :key="`sk-${n}`" class="dp-card dp-card--skeleton">
        <span class="dp-sk dp-sk--name"/>
        <span class="dp-sk dp-sk--price"/>
        <span class="dp-sk"/>
        <span class="dp-sk dp-sk--short"/>
      </div>
      <span class="sr-only">{{ $t('doors.common.loadingPlans') }}</span>
    </div>

    <template v-else-if="licenceOnly">
      <div class="dp-licence">
        <div class="dp-licence-text">
          <h3>{{ licence.title }}</h3>
          <p>{{ licence.body }}</p>
        </div>
        <router-link :to="licence.to" class="btn btn-primary">
          <i class="fas fa-comments" aria-hidden="true"/>
          {{ licence.cta }}
        </router-link>
      </div>
    </template>

    <template v-else-if="emphasised.length">
      <!-- One emphasised plan never renders as a full-width slab: it sits at
           normal width beside a compact "See all plans" card. -->
      <div class="dp-grid" :class="gridClass">
        <article
          v-for="(plan, i) in emphasised"
          :key="plan.id"
          class="dp-card"
          :class="{ 'is-lead': i === 0 }"
        >
          <header class="dp-card-head">
            <h3>{{ plan.name }}</h3>
            <p class="dp-price">
              <template v-if="price(plan)">
                <span class="dp-amount">{{ symbol }}{{ fmt(price(plan).price) }}</span>
                <span class="dp-period">{{ $t('doors.common.perMonth') }}</span>
              </template>
              <span v-else class="dp-amount dp-amount--na">{{ $t('doors.common.pricingOnRequest') }}</span>
            </p>
          </header>
          <p v-if="plan.description" class="dp-desc">{{ plan.description }}</p>
          <ul class="dp-specs">
            <li v-if="plan.cpu"><i class="fas fa-microchip" aria-hidden="true"/> {{ plan.cpu }} vCPU</li>
            <li v-if="plan.ram_gb"><i class="fas fa-memory" aria-hidden="true"/> {{ plan.ram_gb }} GB RAM</li>
            <li v-if="plan.storage_gb"><i class="fas fa-hdd" aria-hidden="true"/> {{ plan.storage_gb }} GB storage</li>
          </ul>
          <button type="button"
class="btn"
:class="i === 0 ? 'btn-primary' : 'btn-outline'"
@click="$emit('select', plan)">
            {{ plan.tier_type === 'membership' ? $t('doors.common.signUpStarter') : $t('doors.common.buy') }}
          </button>
        </article>
        <router-link v-if="showAllCard" to="/pricing" class="dp-card dp-card--all">
          <span class="dp-all-title">{{ $t('homeDoors.seeAllCard.title') }}</span>
          <span class="dp-all-body">{{ $t('homeDoors.seeAllCard.body') }}</span>
          <span v-if="collapsed.length" class="dp-all-names">
            {{ $t('homeDoors.seeAllCard.more', { names: collapsed.map((p) => p.name).join(', ') }) }}
          </span>
          <span class="dp-all-go"><i class="fas fa-arrow-right" aria-hidden="true"/></span>
        </router-link>
      </div>
      <p v-if="note" class="dp-note">{{ note }}</p>
      <p v-if="!showAllCard" class="dp-more">
        <router-link to="/pricing">
          {{ collapsed.length ? $t('doors.common.needMore') : $t('doors.common.seeAllPlans') }}
          <span v-if="collapsed.length" class="dp-more-names">{{ collapsed.map((p) => p.name).join(', ') }}</span>
          <i class="fas fa-arrow-right" aria-hidden="true"/>
        </router-link>
      </p>
    </template>

    <p v-else class="dp-note">
      {{ $t('doors.common.plansUnavailable') }}
      <router-link to="/pricing">{{ $t('doors.common.seeAllPlans') }}</router-link>
    </p>
  </div>
</template>

<script>
import { emphasisePlans, priceFor, formatAmount, CURRENCY_SYMBOLS } from '@/utils/plans-source'

export default {
  name: 'DoorPlans',
  props: {
    plans: { type: Array, default: () => [] },
    doorPlans: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    currency: { type: String, default: 'EUR' },
    note: { type: String, default: '' },
    /** { title, body, cta, to } — rendered when doorPlans is empty. */
    licence: { type: Object, default: null },
    /** 'start' (door pages) or 'center' (homepage). */
    align: { type: String, default: 'start' }
  },
  emits: ['select'],
  computed: {
    licenceOnly() {
      return this.doorPlans.length === 0 && this.licence
    },
    split() {
      return emphasisePlans(this.plans, this.doorPlans)
    },
    emphasised() { return this.split.emphasised },
    collapsed() { return this.split.collapsed },
    symbol() { return CURRENCY_SYMBOLS[this.currency] || this.currency },
    /** Exactly one emphasised plan → pair it with the "See all plans" card. */
    showAllCard() { return this.emphasised.length === 1 },
    gridClass() {
      if (this.showAllCard) return 'dp-grid--single'
      return `dp-grid--${Math.min(this.emphasised.length, 3)}`
    },
    skeletonCount() { return Math.max(1, Math.min(this.doorPlans.length || 2, 3)) }
  },
  methods: {
    price(plan) { return priceFor(plan, this.currency) },
    fmt(v) { return formatAmount(v) }
  }
}
</script>

<style scoped>
.dp { display: flex; flex-direction: column; gap: 1rem; }

.dp-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

@media (min-width: 640px) {
  .dp-grid--2, .dp-grid--3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .dp-grid--single { grid-template-columns: minmax(0, 360px) minmax(0, 280px); }
  .dp-grid--2 { max-width: 820px; }
}

.dp--center { align-items: center; text-align: left; }
.dp--center .dp-grid { width: 100%; justify-content: center; }
.dp--center .dp-grid--2 { margin: 0 auto; }
.dp--center .dp-note, .dp--center .dp-more { text-align: center; }

/* "See all plans" — a quiet card, dashed edge: it is a way on, not a plan. */
.dp-card--all {
  border: 1px dashed var(--ds-neutral-300);
  border-top: 1px dashed var(--ds-neutral-300);
  background: var(--ds-neutral-50);
  text-decoration: none;
  color: var(--ds-neutral-700);
  justify-content: center;
  gap: 0.5rem;
  transition: border-color 0.2s ease, background 0.2s ease;
}
.dp-card--all:hover { border-color: var(--ds-primary-500); background: var(--ds-primary-100); text-decoration: none; }
.dp-card--all:focus-visible { outline: 3px solid var(--ds-accent); outline-offset: 2px; }
.dp-all-title { font-family: var(--ds-font-display); font-weight: 700; font-size: 1.1rem; color: var(--ds-primary-900); }
.dp-all-body { font-size: 0.92rem; line-height: 1.5; }
.dp-all-names { font-size: 0.85rem; color: var(--ds-neutral-500); }
.dp-all-go {
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 50%;
  background: var(--ds-primary-500); color: var(--ds-white);
  margin-top: 0.25rem;
}
@media (min-width: 1024px) {
  .dp-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

.dp-card {
  background: var(--ds-white);
  border: 1px solid var(--ds-neutral-200);
  border-top: 4px solid var(--ds-neutral-200);
  border-radius: var(--ds-radius-lg);
  padding: 1.25rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  min-width: 0;
}

.dp-card.is-lead { border-top-color: var(--ds-primary-500); }

.dp-card-head h3 {
  margin: 0 0 0.25rem;
  font-family: var(--ds-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--ds-primary-900);
}

.dp-price { margin: 0; display: flex; align-items: baseline; gap: 0.35rem; }
.dp-amount {
  font-family: var(--ds-font-display);
  font-size: 2rem;
  font-weight: 700;
  color: var(--ds-primary-900);
  letter-spacing: -0.02em;
}
.dp-amount--na { font-size: 1.1rem; }
.dp-period { color: var(--ds-neutral-500); font-size: 0.9rem; }

.dp-desc { margin: 0; color: var(--ds-neutral-700); font-size: 0.92rem; line-height: 1.5; }

.dp-specs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: var(--ds-neutral-700);
  font-size: 0.9rem;
  flex: 1;
}
.dp-specs i { color: var(--ds-primary-500); width: 1.1rem; }

.dp-card .btn { width: 100%; }

.dp-note { margin: 0; color: var(--ds-neutral-500); font-size: 0.9rem; }
.dp-note a { color: var(--ds-primary-500); }

.dp-more { margin: 0; }
.dp-more a {
  color: var(--ds-primary-500);
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.dp-more-names { color: var(--ds-neutral-500); font-weight: 400; }
.dp-more a:hover { text-decoration: underline; }

/* Licence block (door E) */
.dp-licence {
  background: var(--ds-white);
  border: 1px solid var(--ds-neutral-200);
  border-left: 6px solid var(--ds-primary-900);
  border-radius: var(--ds-radius-lg);
  padding: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 2rem;
}
.dp-licence-text { flex: 1 1 320px; min-width: 0; }
.dp-licence h3 {
  margin: 0 0 0.35rem;
  font-family: var(--ds-font-display);
  font-size: 1.25rem;
  color: var(--ds-primary-900);
}
.dp-licence p { margin: 0; color: var(--ds-neutral-700); line-height: 1.55; }

/* Skeleton */
.dp-card--skeleton { gap: 0.6rem; }
.dp-sk { display: block; height: 0.8rem; border-radius: 4px; background: var(--ds-neutral-100); }
.dp-sk--name { width: 40%; height: 1.1rem; }
.dp-sk--price { width: 55%; height: 2rem; }
.dp-sk--short { width: 60%; }

.sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0);
  white-space: nowrap; border: 0;
}
</style>
